import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { selectCurrentBuilding } from "@/features/cartography/cartographySlice.selectors";
import { buildFlightPayloads } from "./mapper";
import type { FlightWithPOI } from "./types";

const selectFlightInformationFlights = (state: RootState) =>
  state.flightInformation.flights;

const selectSelectedFlightId = (state: RootState) =>
  state.flightInformation.selectedFlightId;

/**
 * All flights with their associated POI IDs computed reactively.
 * Recomputes whenever flights (new poll) or building POIs (building load) change.
 */
export const selectFlightsWithPois = createSelector(
  selectFlightInformationFlights,
  selectCurrentBuilding,
  (flights, currentBuilding): FlightWithPOI[] =>
    buildFlightPayloads(flights, currentBuilding?.pois?.toArray() ?? []),
);

/**
 * The currently selected flight with its POI association always up-to-date.
 * poiId is re-derived from live building POIs on every selector recompute.
 */
export const selectCurrentFlight = createSelector(
  selectFlightsWithPois,
  selectSelectedFlightId,
  (flightsWithPois, selectedId): FlightWithPOI | null => {
    if (!selectedId) return null;
    return (
      flightsWithPois.find((item) => item.flight.id === selectedId) ?? null
    );
  },
);

/**
 * The timestamp of the last flight information refresh.
 */
export const selectFlightInformationRefreshedAt = createSelector(
  (state: RootState) => state.flightInformation.refreshedAt,
  (refreshedAt): number | null => refreshedAt,
);
